// Inline SVG icons for dock-leveler gates on the site map.
//
// Regular gates pick their icon from the catalog (Product.category).
// Leveler gates can't do that — the icon depends on TWO live signals
// (door open/closed AND leveler at-rest/working), and we want them
// styled consistently across customers regardless of which catalog
// they're on. Three states are real; the fourth (door closed +
// leveler working) is physically impossible — a leveler can't be
// engaged against a truck while the door above it is shut — so we
// degrade it to the "closed + at-rest" visual.
//
// Returned as data: URLs so the existing useHtmlImage hook in
// SiteMapCanvas loads them through the same code path as catalog
// images — no special-casing in the renderer.

// Shared palette:
//   #374151 = slate-700 → outlines and door slats
//   #94a3b8 = slate-400 → secondary detail (floor line)
//   #d1d5db = slate-300 → door body fill
//   #16a34a = green-600 → "at rest" tint on the leveler platform
//   #ea580c = orange-600 → "working" tint on the leveler platform
//   #475569 = slate-600 → truck rear hint

const SVG_CLOSED_AT_REST = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 56 56">
  <rect x="10" y="6" width="36" height="36" rx="2" fill="#d1d5db" stroke="#374151" stroke-width="1.5"/>
  <line x1="12" y1="14" x2="44" y2="14" stroke="#374151" stroke-width="1"/>
  <line x1="12" y1="22" x2="44" y2="22" stroke="#374151" stroke-width="1"/>
  <line x1="12" y1="30" x2="44" y2="30" stroke="#374151" stroke-width="1"/>
  <line x1="12" y1="38" x2="44" y2="38" stroke="#374151" stroke-width="1"/>
  <rect x="6" y="42" width="44" height="5" rx="1" fill="#16a34a" stroke="#374151" stroke-width="1.2"/>
  <line x1="2" y1="50" x2="54" y2="50" stroke="#94a3b8" stroke-width="1.5"/>
</svg>`;

const SVG_OPEN_AT_REST = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 56 56">
  <rect x="10" y="6" width="36" height="8" rx="1.5" fill="#d1d5db" stroke="#374151" stroke-width="1.5"/>
  <line x1="12" y1="9" x2="44" y2="9" stroke="#374151" stroke-width="0.8"/>
  <line x1="12" y1="12" x2="44" y2="12" stroke="#374151" stroke-width="0.8"/>
  <line x1="10" y1="14" x2="10" y2="42" stroke="#374151" stroke-width="1.5"/>
  <line x1="46" y1="14" x2="46" y2="42" stroke="#374151" stroke-width="1.5"/>
  <rect x="6" y="42" width="44" height="5" rx="1" fill="#16a34a" stroke="#374151" stroke-width="1.2"/>
  <line x1="2" y1="50" x2="54" y2="50" stroke="#94a3b8" stroke-width="1.5"/>
</svg>`;

const SVG_OPEN_WORKING = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 56 56">
  <line x1="2" y1="6" x2="16" y2="6" stroke="#374151" stroke-width="1.5"/>
  <line x1="2" y1="6" x2="2" y2="44" stroke="#374151" stroke-width="1.5"/>
  <line x1="16" y1="6" x2="16" y2="14" stroke="#374151" stroke-width="1.5"/>
  <rect x="3" y="7" width="12" height="5" rx="1" fill="#d1d5db" stroke="#374151" stroke-width="1"/>
  <line x1="4" y1="9.5" x2="14" y2="9.5" stroke="#374151" stroke-width="0.5"/>
  <line x1="16" y1="14" x2="16" y2="34" stroke="#374151" stroke-width="0.8" stroke-dasharray="2,1.5"/>
  <rect x="19" y="18" width="24" height="20" rx="1" fill="#e5e7eb" stroke="#374151" stroke-width="1.5"/>
  <line x1="20" y1="20" x2="20" y2="36" stroke="#374151" stroke-width="0.7"/>
  <line x1="21" y1="28" x2="42" y2="28" stroke="#374151" stroke-width="0.4"/>
  <rect x="43" y="22" width="10" height="16" rx="1.5" fill="#94a3b8" stroke="#374151" stroke-width="1.3"/>
  <rect x="46" y="25" width="6" height="5" rx="0.8" fill="#93c5fd" stroke="#374151" stroke-width="0.7"/>
  <polygon points="2,44 2,40 19,33 19,37" fill="#ea580c" stroke="#9a3412" stroke-width="1"/>
  <line x1="5" y1="42" x2="9" y2="40.5" stroke="#9a3412" stroke-width="0.5"/>
  <line x1="11" y1="39.5" x2="15" y2="38" stroke="#9a3412" stroke-width="0.5"/>
  <circle cx="25" cy="42" r="3" fill="#1f2937" stroke="#374151" stroke-width="0.8"/>
  <circle cx="37" cy="42" r="3" fill="#1f2937" stroke="#374151" stroke-width="0.8"/>
  <circle cx="48" cy="42" r="3" fill="#1f2937" stroke="#374151" stroke-width="0.8"/>
  <circle cx="25" cy="42" r="1" fill="#94a3b8"/>
  <circle cx="37" cy="42" r="1" fill="#94a3b8"/>
  <circle cx="48" cy="42" r="1" fill="#94a3b8"/>
  <line x1="2" y1="50" x2="54" y2="50" stroke="#94a3b8" stroke-width="1.2"/>
</svg>`;

function svgDataUrl(svg) {
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg.trim())}`;
}

const ICON_CLOSED_AT_REST = svgDataUrl(SVG_CLOSED_AT_REST);
const ICON_OPEN_AT_REST = svgDataUrl(SVG_OPEN_AT_REST);
const ICON_OPEN_WORKING = svgDataUrl(SVG_OPEN_WORKING);

/**
 * Pick the right leveler icon URL for the gate's current state.
 * Returns null if the gate isn't a leveler gate; callers fall back
 * to the catalog icon in that case.
 */
export function levelerIconFor(gate) {
  if (!gate || !gate.is_continuous_with_leveler) return null;
  const working = gate.leveler_state === 'working';
  if (gate.is_open && working) return ICON_OPEN_WORKING;
  if (gate.is_open) return ICON_OPEN_AT_REST;
  // Door closed: leveler must be at rest (working is physically
  // impossible) — show the closed-at-rest icon regardless of what
  // the leveler signal happens to report.
  return ICON_CLOSED_AT_REST;
}

/**
 * Hebrew label for the current leveler state, for tooltips and
 * the gate-details card. Returns null for non-leveler gates so
 * callers can hide the row entirely.
 */
export function levelerStateLabel(gate) {
  if (!gate || !gate.is_continuous_with_leveler) return null;
  return gate.leveler_state === 'working' ? 'בעבודה' : 'במנוחה';
}
